import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Wind, Eye, Feather, Timer, Play, Pause, RotateCcw, Check } from 'lucide-react';

const BREATHING_EXERCISES = [
  { 
    id: 'box', 
    name: 'Box Breathing', 
    description: 'Inhale, hold, exhale, hold - each for 4 seconds',
    pattern: [4, 4, 4, 4],
    labels: ['Inhale', 'Hold', 'Exhale', 'Hold'],
    rounds: 4
  },
  { 
    id: '478', 
    name: '4-7-8 Relaxation', 
    description: 'A natural tranquilizer for the nervous system',
    pattern: [4, 7, 8, 0],
    labels: ['Inhale', 'Hold', 'Exhale', ''],
    rounds: 4
  },
  { 
    id: 'calm', 
    name: 'Calming Breath', 
    description: 'Simple deep breathing for quick relief',
    pattern: [5, 0, 5, 0],
    labels: ['Inhale', '', 'Exhale', ''],
    rounds: 6
  },
];

const GROUNDING_EXERCISES = [
  {
    id: '54321',
    name: '5-4-3-2-1 Technique',
    description: 'Engage your senses to ground yourself in the present',
    steps: [
      { count: 5, sense: 'things you can SEE', icon: Eye },
      { count: 4, sense: 'things you can TOUCH', icon: Feather },
      { count: 3, sense: 'things you can HEAR', icon: Wind },
      { count: 2, sense: 'things you can SMELL', icon: Wind },
      { count: 1, sense: 'thing you can TASTE', icon: Wind },
    ]
  }
];

const JOURNAL_PROMPTS = [
  "What am I grateful for today?",
  "What emotion am I feeling right now, and where do I feel it in my body?",
  "What would I tell a friend who was feeling this way?",
  "What is one small thing I can do for myself today?",
  "What went well today, even if it seems small?",
  "What do I need right now that I'm not getting?",
  "If I could let go of one worry, what would it be?",
  "What makes me feel safe and at peace?",
];

const BreathingExercise = ({ exercise }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [timeLeft, setTimeLeft] = useState(exercise.pattern[0]);
  const [currentRound, setCurrentRound] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && !isComplete) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Move to next phase
            const nextPhase = (currentPhase + 1) % exercise.pattern.length;
            
            // Skip phases with 0 duration
            if (exercise.pattern[nextPhase] === 0) {
              const skipPhase = (nextPhase + 1) % exercise.pattern.length;
              if (skipPhase === 0) {
                // Completed a round
                if (currentRound >= exercise.rounds) {
                  setIsComplete(true);
                  setIsActive(false);
                  return 0;
                }
                setCurrentRound(r => r + 1);
              }
              setCurrentPhase(skipPhase);
              return exercise.pattern[skipPhase];
            }
            
            if (nextPhase === 0) {
              // Completed a round
              if (currentRound >= exercise.rounds) {
                setIsComplete(true);
                setIsActive(false);
                return 0;
              }
              setCurrentRound(r => r + 1);
            }
            setCurrentPhase(nextPhase);
            return exercise.pattern[nextPhase];
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive, currentPhase, currentRound, exercise, isComplete]);

  const reset = () => {
    setIsActive(false);
    setCurrentPhase(0);
    setTimeLeft(exercise.pattern[0]);
    setCurrentRound(1);
    setIsComplete(false);
  };

  const circleProgress = ((exercise.pattern[currentPhase] - timeLeft) / exercise.pattern[currentPhase]) * 100;

  return (
    <Card className="shadow-card border-0">
      <CardHeader className="text-center px-4 sm:px-6 pb-2">
        <CardTitle className="text-base sm:text-lg">{exercise.name}</CardTitle>
        <CardDescription className="text-xs sm:text-sm">{exercise.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
        {/* Visual Circle */}
        <div className="flex justify-center">
          <div className="relative w-36 h-36 sm:w-48 sm:h-48">
            {/* Background circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 192 192">
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="hsl(30, 10%, 90%)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="hsl(155, 14%, 55%)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={553}
                strokeDashoffset={553 - (553 * circleProgress) / 100}
                className="transition-all duration-1000 ease-linear"
              />
            </svg>
            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isComplete ? (
                <div className="text-center animate-fade-in">
                  <Check className="w-8 h-8 sm:w-12 sm:h-12 text-primary mx-auto mb-1 sm:mb-2" />
                  <p className="text-sm sm:text-lg font-semibold">Complete!</p>
                </div>
              ) : (
                <>
                  <p className="text-2xl sm:text-4xl font-bold text-foreground">{timeLeft}</p>
                  <p className="text-sm sm:text-lg font-medium text-primary">{exercise.labels[currentPhase]}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Round {currentRound}/{exercise.rounds}</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-2 sm:gap-4">
          <Button
            variant={isActive ? "outline" : "default"}
            onClick={() => setIsActive(!isActive)}
            className="rounded-full w-24 sm:w-32 text-sm"
            disabled={isComplete}
            data-testid={`breathing-${exercise.id}-toggle`}
          >
            {isActive ? <Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> : <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />}
            {isActive ? 'Pause' : 'Start'}
          </Button>
          <Button
            variant="outline"
            onClick={reset}
            className="rounded-full"
            data-testid={`breathing-${exercise.id}-reset`}
          >
            <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const GroundingExercise = ({ exercise }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState([]);

  const handleComplete = () => {
    if (!completed.includes(currentStep)) {
      setCompleted([...completed, currentStep]);
    }
    if (currentStep < exercise.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setCompleted([]);
  };

  const allComplete = completed.length === exercise.steps.length;

  return (
    <Card className="shadow-card border-0">
      <CardHeader className="px-4 sm:px-6 pb-2">
        <CardTitle className="text-base sm:text-lg">{exercise.name}</CardTitle>
        <CardDescription className="text-xs sm:text-sm">{exercise.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
        {/* Progress */}
        <Progress value={(completed.length / exercise.steps.length) * 100} className="h-2" />

        {/* Steps */}
        <div className="space-y-2 sm:space-y-3">
          {exercise.steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isDone = completed.includes(index);
            
            return (
              <div
                key={index}
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${
                  isActive ? 'bg-primary/10 ring-2 ring-primary' :
                  isDone ? 'bg-muted/30' : 'bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl font-bold ${
                    isDone ? 'bg-primary text-primary-foreground' : 'bg-background'
                  }`}>
                    {isDone ? <Check className="w-5 h-5 sm:w-6 sm:h-6" /> : step.count}
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs sm:text-sm font-medium ${isActive ? 'text-primary' : 'text-foreground'}`}>
                      Name {step.count} {step.sense}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex gap-3">
          {allComplete ? (
            <Button onClick={reset} className="w-full rounded-full text-sm" data-testid="grounding-reset">
              <RotateCcw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          ) : (
            <Button onClick={handleComplete} className="w-full rounded-full btn-hover text-sm" data-testid="grounding-next">
              {completed.includes(currentStep) ? 'Next Step' : 'Done with this step'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const MindfulnessTimer = () => {
  const [duration, setDuration] = useState(5);
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isActive, setIsActive] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsActive(false);
            setIsComplete(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive, timeLeft]);

  const reset = () => {
    setIsActive(false);
    setTimeLeft(duration * 60);
    setIsComplete(false);
  };

  const setNewDuration = (mins) => {
    setDuration(mins);
    setTimeLeft(mins * 60);
    setIsActive(false);
    setIsComplete(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((duration * 60 - timeLeft) / (duration * 60)) * 100;

  return (
    <Card className="shadow-card border-0">
      <CardHeader className="text-center px-4 sm:px-6 pb-2">
        <CardTitle className="flex items-center justify-center gap-2 text-base sm:text-lg">
          <Timer className="w-4 h-4 sm:w-5 sm:h-5" />
          Mindfulness Timer
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">Take a moment to be present</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
        {/* Timer Display */}
        <div className="flex justify-center">
          <div className="relative w-36 h-36 sm:w-48 sm:h-48">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 192 192">
              <circle cx="96" cy="96" r="88" stroke="hsl(30, 10%, 90%)" strokeWidth="8" fill="none" />
              <circle
                cx="96" cy="96" r="88"
                stroke="hsl(27, 52%, 64%)"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={553}
                strokeDashoffset={553 - (553 * progress) / 100}
                className="transition-all duration-1000 ease-linear"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isComplete ? (
                <div className="text-center animate-fade-in">
                  <Check className="w-8 h-8 sm:w-12 sm:h-12 text-accent mx-auto mb-1 sm:mb-2" />
                  <p className="text-sm sm:text-lg font-semibold">Well done!</p>
                </div>
              ) : (
                <p className="text-2xl sm:text-4xl font-bold text-foreground">{formatTime(timeLeft)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Duration Selector */}
        <div className="flex justify-center gap-1.5 sm:gap-2">
          {[1, 3, 5, 10].map((mins) => (
            <Button
              key={mins}
              variant={duration === mins ? "default" : "outline"}
              size="sm"
              onClick={() => setNewDuration(mins)}
              className="rounded-full px-3 sm:px-4 text-xs sm:text-sm"
              disabled={isActive}
              data-testid={`timer-${mins}min`}
            >
              {mins}m
            </Button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-2 sm:gap-4">
          <Button
            variant={isActive ? "outline" : "default"}
            onClick={() => setIsActive(!isActive)}
            className="rounded-full w-24 sm:w-32 text-sm"
            disabled={isComplete}
            data-testid="timer-toggle"
          >
            {isActive ? <Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> : <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />}
            {isActive ? 'Pause' : 'Start'}
          </Button>
          <Button variant="outline" onClick={reset} className="rounded-full" data-testid="timer-reset">
            <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Exercises = () => {
  const navigate = useNavigate();
  const [randomPrompt, setRandomPrompt] = useState(JOURNAL_PROMPTS[0]);

  const getNewPrompt = () => {
    const newPrompt = JOURNAL_PROMPTS[Math.floor(Math.random() * JOURNAL_PROMPTS.length)];
    setRandomPrompt(newPrompt);
  };

  return (
    <div className="min-h-screen bg-background grain">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center">
          <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} className="rounded-full h-9 w-9 sm:h-10 sm:w-10" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <h1 className="text-lg sm:text-xl font-heading font-bold ml-3 sm:ml-4">Exercises & Tools</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs defaultValue="breathing" className="space-y-6 sm:space-y-8">
          <TabsList className="bg-muted/50 p-1 rounded-xl sm:rounded-2xl w-full grid grid-cols-4 gap-1">
            <TabsTrigger value="breathing" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-1 sm:px-3" data-testid="tab-breathing">
              <Wind className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Breathing</span>
            </TabsTrigger>
            <TabsTrigger value="grounding" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-1 sm:px-3" data-testid="tab-grounding">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Grounding</span>
            </TabsTrigger>
            <TabsTrigger value="mindfulness" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-1 sm:px-3" data-testid="tab-mindfulness">
              <Timer className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Mindfulness</span>
            </TabsTrigger>
            <TabsTrigger value="prompts" className="rounded-lg sm:rounded-xl text-xs sm:text-sm px-1 sm:px-3" data-testid="tab-prompts">
              <Feather className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Prompts</span>
            </TabsTrigger>
          </TabsList>

          {/* Breathing */}
          <TabsContent value="breathing" className="space-y-4 sm:space-y-6">
            <p className="text-xs sm:text-sm text-muted-foreground text-center max-w-lg mx-auto px-2">
              Breathing exercises can help calm your nervous system and reduce anxiety.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {BREATHING_EXERCISES.map((exercise) => (
                <BreathingExercise key={exercise.id} exercise={exercise} />
              ))}
            </div>
          </TabsContent>

          {/* Grounding */}
          <TabsContent value="grounding" className="space-y-4 sm:space-y-6">
            <p className="text-xs sm:text-sm text-muted-foreground text-center max-w-lg mx-auto px-2">
              Grounding techniques help bring you back to the present moment.
            </p>
            <div className="max-w-xl mx-auto">
              {GROUNDING_EXERCISES.map((exercise) => (
                <GroundingExercise key={exercise.id} exercise={exercise} />
              ))}
            </div>
          </TabsContent>

          {/* Mindfulness */}
          <TabsContent value="mindfulness" className="space-y-4 sm:space-y-6">
            <p className="text-xs sm:text-sm text-muted-foreground text-center max-w-lg mx-auto px-2">
              Take a few minutes to simply be present and focus on your breath.
            </p>
            <div className="max-w-md mx-auto">
              <MindfulnessTimer />
            </div>
          </TabsContent>

          {/* Journal Prompts */}
          <TabsContent value="prompts" className="space-y-4 sm:space-y-6">
            <p className="text-xs sm:text-sm text-muted-foreground text-center max-w-lg mx-auto px-2">
              Journaling prompts to help you reflect and process your thoughts.
            </p>
            <Card className="shadow-card border-0 max-w-xl mx-auto">
              <CardContent className="p-4 sm:p-8 text-center space-y-4 sm:space-y-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Feather className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
                <blockquote className="text-base sm:text-xl font-medium text-foreground italic px-2">
                  "{randomPrompt}"
                </blockquote>
                <Button onClick={getNewPrompt} variant="outline" className="rounded-full text-sm" data-testid="new-prompt-btn">
                  Get Another Prompt
                </Button>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
              {JOURNAL_PROMPTS.map((prompt, index) => (
                <Card 
                  key={index} 
                  className="shadow-soft border-0 cursor-pointer hover:shadow-card transition-shadow"
                  onClick={() => setRandomPrompt(prompt)}
                >
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-muted-foreground">{prompt}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Exercises;
